import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, ListSkeleton } from "@/components/common";
import { REQUEST_TYPES, label } from "@/lib/constants";
import { fromNow } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, FileCheck, Check, X } from "lucide-react";

const STATUS_CHIP = { pending: "bg-amber-100 text-amber-700", approved: "bg-emerald-100 text-emerald-700", rejected: "bg-rose-100 text-rose-700" };
const STATUS_LABEL = { pending: "Beklemede", approved: "Onaylandı", rejected: "Reddedildi" };

export default function Requests() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "purchase", title: "", amount: "", reason: "", approver_id: "" });

  const { data = [], isLoading } = useQuery({ queryKey: ["requests"], queryFn: async () => { const d = (await api.get("/requests")).data; return Array.isArray(d) ? d : []; } });
  const { data: members = [] } = useQuery({ queryKey: ["members"], queryFn: async () => (await api.get("/org/members")).data });
  const canApprove = can("manage_settings") || can("view_reports");

  const create = async () => {
    if (!form.title.trim()) { toast.error("Başlık gerekli"); return; }
    try {
      await api.post("/requests", { type: form.type, title: form.title, approver_id: form.approver_id || null, fields: { tutar: form.amount, gerekçe: form.reason } });
      toast.success("Talep oluşturuldu");
      qc.invalidateQueries({ queryKey: ["requests"] });
      setOpen(false); setForm({ type: "purchase", title: "", amount: "", reason: "", approver_id: "" });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const decide = async (id, decision) => {
    let comment = "";
    if (decision === "rejected") { comment = prompt("Red gerekçesi:") || ""; }
    await api.post(`/requests/${id}/decision`, { decision, comment });
    toast.success(decision === "approved" ? "Talep onaylandı" : "Talep reddedildi");
    qc.invalidateQueries({ queryKey: ["requests"] });
  };

  const pending = data.filter((r) => r.status === "pending");
  const resolved = data.filter((r) => r.status !== "pending");

  const Row = ({ r }) => (
    <div className="flex items-center gap-3 p-3.5 border-b" data-testid={`request-row-${r.id}`}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{r.title}</p>
        <p className="text-xs text-muted-foreground">{label(REQUEST_TYPES, r.type)} • {r.created_by_name} • {fromNow(r.created_at)}</p>
      </div>
      <Badge variant="outline" className={`text-[10px] ${STATUS_CHIP[r.status]}`}>{STATUS_LABEL[r.status]}</Badge>
      {r.status === "pending" && canApprove && (
        <div className="flex gap-1">
          <Button size="icon" variant="outline" className="h-8 w-8 text-emerald-600" onClick={() => decide(r.id, "approved")} data-testid={`approve-${r.id}`}><Check className="h-4 w-4" /></Button>
          <Button size="icon" variant="outline" className="h-8 w-8 text-rose-600" onClick={() => decide(r.id, "rejected")} data-testid={`reject-${r.id}`}><X className="h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );

  return (
    <div>
      <PageHeader title="Talepler ve Onaylar" subtitle={`${pending.length} bekleyen talep`}
        actions={<Button onClick={() => setOpen(true)} data-testid="new-request-button"><Plus className="h-4 w-4 mr-1.5" /> Yeni Talep</Button>} />

      {isLoading ? <ListSkeleton /> : (
        <Tabs defaultValue="pending">
          <TabsList>
            <TabsTrigger value="pending" data-testid="req-tab-pending">Bekleyen ({pending.length})</TabsTrigger>
            <TabsTrigger value="resolved" data-testid="req-tab-resolved">Sonuçlanan ({resolved.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="pending" className="mt-4"><Card>{pending.length ? pending.map((r) => <Row key={r.id} r={r} />) : <EmptyState icon={FileCheck} title="Bekleyen talep yok" />}</Card></TabsContent>
          <TabsContent value="resolved" className="mt-4"><Card>{resolved.length ? resolved.map((r) => <Row key={r.id} r={r} />) : <EmptyState icon={FileCheck} title="Sonuçlanan talep yok" />}</Card></TabsContent>
        </Tabs>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="request-dialog">
          <DialogHeader><DialogTitle>Yeni Talep</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Talep Türü</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="request-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{REQUEST_TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Başlık</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" data-testid="request-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tutar</Label><Input value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5" placeholder="5000 TL" /></div>
              <div><Label>Onaylayan</Label>
                <Select value={form.approver_id || "none"} onValueChange={(v) => setForm({ ...form, approver_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Seçin" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Yok</SelectItem>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Gerekçe</Label><Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>İptal</Button><Button onClick={create} data-testid="request-save-button">Gönder</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
